// src/services/spotifyService.ts
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';
import { getCurrentArtistId } from './authService';
import { isAuthenticated, getAccessToken } from './authService';

// Interface for Spotify Artist Metrics
export interface SpotifyArtistMetrics {
  streams: number;
  listeners: number;
  followers: number;
  saveRate: number;
  shares: number;
  playlistAdds: number;
  avgStreamLength: number;
  topCountries: {
    countryCode: string;
    countryName: string;
    streams: number;
    percentChange: number;
  }[];
  period: string; // 'last-28-days', '30-days', 'last-week', etc.
}

// Interface for Spotify Song Metrics
export interface SpotifySongMetrics {
  id: string;
  name: string;
  streams: number;
  saves: number;
  shares: number;
  playlistAdds: number;
  skipRate: number;
  completionRate: number;
}

// Fetch artist metrics from Spotify
export const fetchArtistMetrics = async (period: string = 'last-28-days'): Promise<SpotifyArtistMetrics | null> => {
  try {
    const artistId = getCurrentArtistId();
    if (!artistId) {
      throw new Error('No artist ID available');
    }
    
    // This endpoint would call your backend, which then calls Spotify API
    const response = await axios.get(
      getApiUrl(API_CONFIG.ENDPOINTS.SPOTIFY.ARTIST_STATS), 
      { params: { artistId, period } }
    );
    
    if (response.data) {
      return response.data as SpotifyArtistMetrics;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching artist metrics from Spotify:', error);
    return null;
  }
};

// Fetch track metrics from Spotify
export const fetchTrackMetrics = async (trackIds: string[], period: string = 'last-28-days'): Promise<SpotifySongMetrics[]> => {
  try {
    const artistId = getCurrentArtistId();
    if (!artistId) {
      throw new Error('No artist ID available');
    }
    
    // This endpoint would call your backend, which then calls Spotify API
    const response = await axios.post(
      getApiUrl(API_CONFIG.ENDPOINTS.SPOTIFY.FETCH_METRICS), 
      { artistId, trackIds, period }
    );
    
    if (response.data && Array.isArray(response.data)) {
      return response.data as SpotifySongMetrics[];
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching track metrics from Spotify:', error);
    return [];
  }
};
// Add these functions to src/services/spotifyService.ts

/**
 * Fetch artist's top tracks from Spotify
 */
export const fetchArtistTracks = async (
  spotifyArtistId: string, 
  limit: number = 10,
  offset: number = 0
): Promise<{
  items: any[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}> => {
  try {
    if (!isAuthenticated()) {
      console.error('User is not authenticated');
      return { items: [], total: 0, offset, limit, hasMore: false };
    }
    
    const token = getAccessToken();
    
    const response = await axios.get(
      getApiUrl(`/api/spotify/artist-tracks/${spotifyArtistId}`), 
      { 
        params: { limit, offset },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    return response.data || { items: [], total: 0, offset, limit, hasMore: false };
  } catch (error) {
    console.error('Error fetching artist tracks:', error);
    return { items: [], total: 0, offset, limit, hasMore: false };
  }
};

export const fetchAllArtistTracks = async (spotifyArtistId: string): Promise<any[]> => {
  let allTracks: any[] = [];
  let offset = 0;
  const limit = 50; // Maximal möglicher Wert pro Anfrage
  let hasMore = true;
  
  while (hasMore) {
    try {
      // Verwende die bestehende Funktion mit Pagination
      const response = await fetchArtistTracks(spotifyArtistId, limit, offset);
      
      // Tracks zur Gesamtliste hinzufügen
      if (response.items && Array.isArray(response.items)) {
        allTracks = [...allTracks, ...response.items];
      }
      
      // Überprüfen, ob es weitere Tracks gibt
      hasMore = response.hasMore;
      
      // Offset für die nächste Anfrage erhöhen
      offset += limit;
      
      // Optional: Status-Update (z.B. für Fortschrittsanzeige)
      console.log(`Loaded ${allTracks.length} of ${response.total} tracks...`);
      
      // Sicherheits-Timeout, falls die API nicht korrekt paginiert
      if (allTracks.length > 1000) {
        console.warn('Mehr als 1000 Tracks geladen, Abbruch um Endlosschleife zu vermeiden');
        break;
      }
      
      // Optional: Kurze Verzögerung zwischen Anfragen, um API-Limits nicht zu überschreiten
      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    } catch (error) {
      console.error('Fehler beim Laden aller Tracks:', error);
      break;
    }
  }
  
  return allTracks;
};

// Funktion zum Abrufen aller Tracks für alle Alben eines Künstlers
// Funktion zum Abrufen aller Tracks für alle Alben eines Künstlers
export const fetchAllTracksForArtist = async (spotifyArtistId: string): Promise<any[]> => {
  try {
    // 1. Alle Alben des Künstlers abrufen
    const albums = await fetchArtistReleases(spotifyArtistId, 50);
    let allTracks: any[] = [];
    
    // 2. Für jedes Album alle Tracks laden
    for (const album of albums) {
      console.log(`Loading tracks for album: ${album.name}`);
      let albumTracks: any[] = [];
      let offset = 0;
      let hasMore = true;
      
      // 3. Pagination für Tracks innerhalb eines Albums
      while (hasMore) {
        try {
          const response = await axios.get(
            getApiUrl(`/api/spotify/album-tracks/${album.id}`),
            {
              params: { limit: 50, offset },
              headers: {
                'Authorization': `Bearer ${getAccessToken()}`
              }
            }
          );
          
          const data = response.data;
          
          if (data.items && Array.isArray(data.items)) {
            // Füge Album-Informationen zu jedem Track hinzu
            const tracksWithAlbumInfo = data.items.map((track: any) => ({
              ...track,
              album: {
                id: album.id,
                name: album.name,
                releaseDate: album.date,
                type: album.type
              }
            }));
            
            albumTracks = [...albumTracks, ...tracksWithAlbumInfo];
          }
          
          hasMore = data.hasMore;
          offset += 50;
          
          // Sicherheits-Timeout
          if (albumTracks.length > 500) {
            console.warn(`Album ${album.name} hat mehr als 500 Tracks, Abbruch der Pagination`);
            break;
          }
        } catch (error) {
          console.error(`Fehler beim Laden der Tracks für Album ${album.name}:`, error);
          break;
        }
      }
      
      allTracks = [...allTracks, ...albumTracks];
      
      // Kurze Verzögerung zwischen Album-Anfragen
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Entferne Duplikate basierend auf Track-ID
    const uniqueTracks = Array.from(
      new Map(allTracks.map(track => [track.id, track])).values()
    );
    
    console.log(`Loaded ${uniqueTracks.length} unique tracks from ${albums.length} albums`);
    return uniqueTracks;
  } catch (error) {
    console.error('Fehler beim Laden aller Tracks für den Künstler:', error);
    return [];
  }
};
/**
 * Fetch artist's releases (albums, singles, etc.) from Spotify
 */
export const fetchArtistReleases = async (spotifyArtistId: string, limit: number = 10): Promise<any[]> => {
  try {
    if (!isAuthenticated()) {
      console.error('User is not authenticated');
      return [];
    }
    
    const token = getAccessToken();
    
    const response = await axios.get(
      getApiUrl(`/api/spotify/artist-releases/${spotifyArtistId}`), 
      { 
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    return response.data || [];
  } catch (error) {
    console.error('Error fetching artist releases:', error);
    return [];
  }
};
/**
 * Aktualisiert Songs und Releases eines Artists aus Spotify
 */
export const updateArtistDataFromSpotify = async (artistId: string, spotifyId: string): Promise<any> => {
  try {
    if (!isAuthenticated()) {
      throw new Error('Benutzer ist nicht angemeldet');
    }
    
    const token = getAccessToken();
    
    const response = await axios.post(
      getApiUrl('/api/spotify/update-artist-data'),
      { artistId, spotifyId },
      { 
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Fehler beim Aktualisieren der Artist-Daten:', error);
    throw error;
  }
};
// Import metrics directly from Spotify into your app
export const importSpotifyMetrics = async (): Promise<boolean> => {
  try {
    const artistId = getCurrentArtistId();
    if (!artistId) {
      throw new Error('No artist ID available');
    }
    
    // Fetch the latest metrics from Spotify
    const metrics = await fetchArtistMetrics();
    if (!metrics) {
      return false;
    }
    
    // Convert Spotify metrics format to our app's format
    const appMetrics = {
      streams: metrics.streams,
      listeners: metrics.listeners,
      follower: metrics.followers,
      saves: Math.round(metrics.streams * (metrics.saveRate / 100)),
      playlist_adds: metrics.playlistAdds,
      notes: `Automatically imported from Spotify on ${new Date().toLocaleDateString()}`
    };
    
    // Submit these metrics to our API
    const response = await axios.post(
      getApiUrl(API_CONFIG.ENDPOINTS.ARTIST.METRICS('me')),
      appMetrics
    );
    
    return response.data && response.data.success;
  } catch (error) {
    console.error('Error importing Spotify metrics:', error);
    return false;
  }
};

// Check if we can directly import data from Spotify
export const canImportFromSpotify = async (): Promise<boolean> => {
  try {
    const artistId = getCurrentArtistId();
    return !!artistId;
  } catch (error) {
    return false;
  }
};